export type CalibrationRunStatus =
  | 'COMPLETED'
  | 'INITIATED'
  | 'IN_PROGRESS'
  | 'FAILED'
  | 'STOPPED'
  | 'CANCELLED'
  | string;

import type { AISUser } from '@frontend-platform/user-directory';

export interface ApiMetric {
  id: string;
  displayName: string;
  metricKey: string | null;
  type: 'PREDEFINED' | 'CUSTOM' | string;
  metadata: Record<string, unknown>;
}

// One entry from GET /observation-run/metrics — the metric library (predefined
// + custom). `updatedBy` arrives as a raw user id and is resolved to an AISUser
// by the fetch hook.
export interface CalibrationMetric {
  id: string;
  displayName: string;
  description?: string;
  metricKey: string | null;
  type: 'PREDEFINED' | 'CUSTOM' | string;
  category: string;
  metadata?: Record<string, unknown>;
  usedInRuns?: number;
  createdBy?: AISUser | string;
  updatedBy?: AISUser | string;
  createdAt?: string | number;
  updatedAt?: string | number;
}

// Stable identifier for a metric: predefined use metricKey, customs use id
// (their metricKey is null).
export const getMetricId = (metric: Pick<CalibrationMetric, 'metricKey' | 'id'>): string =>
  metric.metricKey || metric.id;

export interface SelectedMetricGroup {
  category: string;
  metrics: ApiMetric[];
  selectedCriteriaCount: number;
  totalCriteriaCount: number;
}

export interface CalibrationRun {
  id: number;
  botId: string;
  botReferenceId: string;
  runId: string;
  runName: string;
  dataSetName?: string;
  env: string;
  status: CalibrationRunStatus;
  startTime?: string;
  endTime?: string;
  conversationCount?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: AISUser;
  updatedBy: AISUser;
  isActive: boolean;
  isDeleted: boolean;
  evaluationBotRefId?: string;
  historicalBotRefId?: string;
  evaluationEnvironment?: string;
  historicalEnvironment?: string;
  /** Manifest of the build this run evaluated (EP bots only). */
  evaluationManifestId?: string;
  /** Build identifier, e.g. `Build_V22 draft`. Absent for non-EP / pre-EP runs. */
  manifestBuild?: string;
  /** Whether that build is still the env's active build. */
  releaseActive?: boolean;
  /** `DELETED` when the evaluated build no longer exists. */
  releaseAction?: string;
  estimatedCost?: string;
  estimatedAPICalls?: string;
  selectedConversationCount?: string;
  conversationCountThreshold?: string;
  recommendationCount: number;
  selectedMetrics?: SelectedMetricGroup[];
  channel?: string;
  payload?: string;
  /** Live/SCHEDULED runs only — the sample rate and timezone the run was scheduled with. */
  scheduledDataPayload?: { sampleRate?: number; timeZone?: string };
}

export function getCriteriaCount(run: CalibrationRun): number {
  if (!run.selectedMetrics?.length) return 0;
  return run.selectedMetrics.reduce((sum, g) => sum + (g.selectedCriteriaCount ?? 0), 0);
}
